import { ShiftManagementBulkDeleteShiftsDocument } from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { trackDeleteShiftsEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type { Shift } from 'modules/shift_management/lib/types'

import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface useDestroyShiftsProps {
  shifts: Shift[]
  employeeCount: number
  destroyBackupShifts: boolean
  destroyAutomaticOnly: boolean
}

const useDestroyShifts = ({
  shifts,
  employeeCount,
  destroyBackupShifts,
  destroyAutomaticOnly = false,
}: useDestroyShiftsProps) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()
  const shiftIds = uniq(shifts.map((shift) => shift.id))

  const [destroyShifts, { loading }] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const onDestroy = async (closePortal: ClosePortalFn) => {
    const result = await destroyShifts({
      variables: {
        ids: shiftIds,
        destroyBackupShifts,
        destroyAutomaticShifts: destroyAutomaticOnly,
      },
      update: (cache, { data }) => {
        data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
          cache.evict({
            id: cache.identify(shift),
          })
        })
      },
    })
    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      trackDeleteShiftsEvent({
        shifts,
        view,
        employeeCount,
        shiftsConflicts,
        from: 'top_bar_button',
      })

      // Get shiftsRows where there are affected shifts to update stats
      const shiftKeys = new Set(uniq(shifts.map((shift) => shift.rowKeyReference(view))))
      const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
        return shiftKeys.has(row.key)
      })

      await refreshStats(previousShiftsRows)

      const shiftsToRefresh = shifts.filter((shift) => {
        const shouldIncludeByBackup = destroyBackupShifts || !shift.isBackup
        const shouldIncludeByAutomatic = !destroyAutomaticOnly || shift.isAutomatic

        return shouldIncludeByBackup && shouldIncludeByAutomatic
      })

      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: shiftsToRefresh, view })
      dispatchSelection({ type: 'CLEAR_SELECTION' })

      closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.delete_shifts_success'),
      })
    }
  }

  return {
    onDestroy,
    submitting: loading,
  }
}

const useDestroyShift = ({ shift, isBackup }: { shift: Shift; isBackup?: boolean }) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats({ fetchPolicy: 'cache-only' })

  const [destroyShifts, { loading }] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const onDestroy = async (closePortal: ClosePortalFn) => {
    const result = await destroyShifts({
      variables: { ids: [shift.id], destroyBackupShifts: true, destroyAutomaticShifts: false },
      update: (cache, { data }) => {
        data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
          cache.evict({
            id: cache.identify(shift),
          })
        })
      },
    })
    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      trackDeleteShiftsEvent({
        shifts: [shift],
        view,
        employeeCount: 1,
        shiftsConflicts,
        from: 'contextual_button',
        isBackup,
      })

      await refreshStats(shiftsRows)

      dispatchShiftsRows({ type: 'DESTROY_SHIFT', shift, view })

      closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.delete_shift_modal.success'),
      })
    }
  }

  return {
    onDestroy,
    submitting: loading,
  }
}

export { useDestroyShift, useDestroyShifts }
